import cx from 'classnames'
import styles from './DocumentListItem.module.css'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { RadialProgress } from '@/components/RadialProgress'
import { Spinner } from '@/components/Spinner'
import { Pill } from '@/components/Pill'
import { Checkbox } from '@/components/Checkbox'
import { FileTypeIcon } from '@/components/FileTypeIcon'
import { getFileIconType } from '@/utils/icon-util'
import { dateFormatters } from '@/utils/date-util'
import {
  type DocumentActions,
  DocumentItemActionsDropdown,
} from '@/features/documents/components/DocumentItemActionsDropdown'
import { type DocumentListItemStatus } from '@/features/documents/JobDocumentListItemViewModel'

export interface DocumentListItemProps {
  title: string
  archived: boolean
  isPurged: boolean
  isUploading: boolean
  isManualAttestationRequired: boolean
  dateModified: Date
  author: AvatarSubject
  typeName?: string
  status: DocumentListItemStatus | null
  /**
   * `null` hides the actions dropdown.
   */
  actions: DocumentActions | null
  onPress: () => unknown
  selectable?: boolean
  selected?: boolean
  onToggleSelected?: () => unknown
  spacing?: boolean
  showIcon?: boolean
}

export function DocumentListItem({
  title,
  archived,
  isPurged,
  isUploading,
  isManualAttestationRequired,
  dateModified,
  author,
  typeName,
  status,
  actions,
  onPress,
  selectable,
  selected,
  onToggleSelected,
  spacing,
  showIcon = true,
}: DocumentListItemProps) {
  const icon = getFileIconType(title || '')

  return (
    <div
      className={cx(styles.root, spacing && styles.spacing)}
      data-test={`document-list-item-${title}`}
    >
      {selectable && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            checked={selected}
            isDisabled={archived || isPurged}
            onChange={() => onToggleSelected?.()}
          />
        </div>
      )}
      <div className={styles.itemContainer}>
        <RawButton className={styles.main} onPress={() => onPress()}>
          {showIcon && (
            <div className={styles.icon}>
              <FileTypeIcon
                height={36}
                primaryColor={icon.color}
                strokeColor="#bcbcbe"
                type={icon.type}
              />
            </div>
          )}
          <div className={styles.info}>
            {archived && (
              <div className={styles.archived}>
                <Pill color="red" shape="square" trim>
                  Archived
                </Pill>
              </div>
            )}
            {isPurged && !isUploading && !archived && (
              <div className={styles.metaItem}>
                <Pill color="red" shape="square" trim>
                  Purged
                </Pill>
              </div>
            )}
            {isManualAttestationRequired && !archived && (
              <div className={styles.archived}>
                <Pill color="red" shape="square" trim>
                  Manual Verification Required
                </Pill>
              </div>
            )}
            <div className={styles.title}>
              <span className={styles.primaryTitle}>{title}</span>
            </div>
            <div className={styles.dateModified}>
              <p>{dateFormatters.dateTimeWithoutYear.format(dateModified)}</p>
            </div>
            <div className={styles.meta}>
              <div className={styles.metaItem}>
                <Avatar size={20} subject={author} />
              </div>
              {typeName !== undefined && (
                <div className={styles.metaItem}>
                  <div className={styles.divider} />
                </div>
              )}
              {typeName !== undefined && (
                <div className={styles.metaItem}>
                  <Pill stout>{typeName}</Pill>
                </div>
              )}
            </div>
          </div>
        </RawButton>
        <ItemIcon title={title} status={status} actions={actions} />
      </div>
    </div>
  )
}

interface ItemIconProps {
  title: string
  status: DocumentListItemStatus | null
  actions: DocumentActions | null
}

function ItemIcon({ title, status, actions }: ItemIconProps) {
  switch (status?.type) {
    case 'Uploading':
      return (
        <span className={styles.status}>
          <RadialProgress size={24} progress={status.percent} />
        </span>
      )
    case 'Preprocessing':
      return (
        <span
          className={styles.status}
          title="This document is currently undergoing a virus scan"
        >
          <Spinner />
        </span>
      )
    case 'AttestationScreening':
      return (
        <span
          className={styles.status}
          title="This document is currently undergoing client data verification"
        >
          <Spinner />
        </span>
      )
    case 'ManualAttestationRequired':
      return (
        <span
          className={cx(styles.status, styles.manualVerificationRequired)}
          title="This document requires manual verification before it becomes available"
        >
          <Icon name="alert" />
        </span>
      )
    case 'Quarantined':
      return (
        <span
          className={cx(styles.status, styles.virus)}
          title="This document is infected with a virus and is not available for download"
        >
          <Icon name="virus" />
        </span>
      )
    case 'Failed':
      return (
        <span
          className={cx(styles.status, styles.failed)}
          title="This document has failed to upload"
        >
          <Icon name="info" />
        </span>
      )
  }

  if (actions) {
    return (
      <span className={styles.status}>
        <DocumentItemActionsDropdown
          actions={actions}
          muteClickEvent
          title={title}
        />
      </span>
    )
  }
  return null
}
